/* eslint react-hooks/exhaustive-deps: "warn" */

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './ViesComparisonSection.module.scss';

type Translate = (key: string) => string;

interface CompareRow {
  label: string;
  gdb: string;
  vies: string;
}

const getRows = (t: Translate): CompareRow[] => [
  {
    label: t('Coverage'),
    gdb: t('EU, UK and US alongside national identifiers across 200+ countries — VAT, EIN, GSTIN, ABN, CNPJ, UID.'),
    vies: t('27 EU member states plus Northern Ireland. GB numbers left the system on 31 December 2020.'),
  },
  {
    label: t('Reliability'),
    gdb: t(
      'Company data already sits on our servers, so most answers come straight back from us. Nothing has to be fetched from a government website while you wait.',
    ),
    vies: t(
      'Every query is relayed in real time to the national database of the member state concerned. Speed and success depend entirely on that country’s system when you ask.',
    ),
  },
  {
    label: t('Uptime'),
    gdb: t(
      'You call one endpoint, not twenty-seven. If a government system is down we keep retrying in the background and hand you the result when it lands, so their outage never becomes yours.',
    ),
    vies: t(
      'Outages are routine. The Commission’s own guidance notes the service is periodically unavailable, and when a national backend saturates it rejects requests for every consumer worldwide at once.',
    ),
  },
  {
    label: t('Bulk data'),
    gdb: t('Full files by CSV, S3 or SFTP, a batch API, or the entire vendor and seller master in a single exchange.'),
    vies: t('One number at a time through the portal or a SOAP call. No bulk file and no batch endpoint.'),
  },
  {
    label: t('What comes back'),
    gdb: t(
      'The number resolved to the legal entity in the company register — legal name, registration number, status, officers.',
    ),
    vies: t('Valid or invalid, with the trader name and address only where the member state permits it.'),
  },
  {
    label: t('Monitoring'),
    gdb: t('Daily re-check on every number you hold, with the change pushed into your systems.'),
    vies: t('A point-in-time query. Nothing tells you when a number is cancelled, delisted or reassigned afterwards.'),
  },
  {
    label: t('Record keeping'),
    gdb: t(
      'Every check retained with source, response, timestamp and reference. Exportable by counterparty or by period.',
    ),
    vies: t('A consultation reference at the moment of the check, if you supplied your own VAT number.'),
  },
];

export const ViesComparisonSection = () => {
  const { t } = useInt();
  const rows = getRows(t);

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            title={t('Side by side with the free EU checker')}
            description={t(
              "VIES is the European Commission's free service for checking EU VAT numbers, and we query it too. It answers one question, for one region, one number at a time. Below is how the two compare across the seven things enterprise finance and compliance teams ask about.",
            )}
          />
        </div>

        <div className={styles.tableCard}>
          <div className={styles.table}>
            <div className={cn(styles.headCell, styles.headBlank)} />
            <div className={cn(styles.headCell, styles.headGdb)}>Global Database</div>
            <div className={cn(styles.headCell, styles.headVies)}>VIES</div>

            {rows.map((row, i) => (
              <div key={row.label} className={styles.rowGroup}>
                <div className={cn(styles.rowLabel, i === rows.length - 1 && styles.last)}>{row.label}</div>
                <div className={cn(styles.gdbCell, i === 0 && styles.first, i === rows.length - 1 && styles.last)}>
                  <span className={styles.gdbMark}>✓</span>
                  {row.gdb}
                </div>
                <div className={cn(styles.viesCell, i === rows.length - 1 && styles.last)}>{row.vies}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
